import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../UserContext";
import { Event } from "../Events";
import axios from "axios";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";

interface LiveProps {
  eventData: Event;
}

interface LiveData {
  footTrafficMale: string;
  footTrafficFemale: string;
  trialMale: string;
  trialFemale: string;
  totalSold: string;
  salesHistory: Array<{
    products: Array<{
      productName: string;
      quantity: number;
    }>;
    metadata: {
      gender: string;
      generation: string;
      ethnicity: string;
    };
  }>;
}

const MetricDisplay: React.FC<{
  label: string;
  maleCount: number;
  femaleCount: number;
}> = ({ label, maleCount, femaleCount }) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography variant="h6">{label}</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "grey.200",
          px: 2,
          py: 0.5,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "medium", color: "text.primary" }}
        >
          Total: {maleCount + femaleCount}
        </Typography>
      </Box>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ManIcon color="primary" />
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Male: {maleCount}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <WomanIcon color="primary" />
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Female: {femaleCount}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

const ConversionDisplay: React.FC<{ liveData: LiveData }> = ({ liveData }) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography variant="h6">Conversion</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "grey.200",
          px: 2,
          py: 0.5,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "medium", color: "text.primary" }}
        >
          Total Sold: {liveData.totalSold || 0}
        </Typography>
      </Box>
    </Box>

    <Box sx={{ mt: 2 }}>
      {liveData.salesHistory?.map((sale, index) => (
        <Paper
          key={index}
          sx={{
            p: 1.5,
            mb: 1,
            backgroundColor: "background.default",
          }}
        >
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {sale.metadata.gender} • {sale.metadata.generation} •{" "}
              {sale.metadata.ethnicity}
            </Typography>
          </Box>
          {sale.products.map((product, pidx) => (
            <Typography key={pidx} variant="body2">
              {product.productName}: {product.quantity}
            </Typography>
          ))}
        </Paper>
      ))}
    </Box>
  </Paper>
);

const Results: React.FC<LiveProps> = ({ eventData }) => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [liveData, setLiveData] = useState<LiveData | null>(null);

  useEffect(() => {
    const fetchLiveData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/events/live/${eventData.event_id}`
        );
        setLiveData(response.data);
      } catch (error) {
        console.error("Error fetching live data:", error);
      }
    };

    fetchLiveData();
    const interval = setInterval(fetchLiveData, 5000);
    return () => clearInterval(interval);
  }, [eventData.event_id]);

  if (!liveData) return null;

  return (
    <Box sx={{ mx: 5, mt: 2.5 }}>
      {currentUser?.role === "BA" && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/live", { state: { eventData } })}
          >
            Launch PROMORAVEN Live
          </Button>
        </Box>
      )}

      <MetricDisplay
        label="Foot Traffic"
        maleCount={parseInt(liveData.footTrafficMale) || 0}
        femaleCount={parseInt(liveData.footTrafficFemale) || 0}
      />

      <MetricDisplay
        label="Trial"
        maleCount={parseInt(liveData.trialMale) || 0}
        femaleCount={parseInt(liveData.trialFemale) || 0}
      />

      <ConversionDisplay liveData={liveData} />
    </Box>
  );
};

export default Results;
